import { WizardFormData } from '../IntegrationWizard'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AlertCircle, Lightbulb } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface Step2IntentRoutingProps {
  formData: WizardFormData
  setFormData: (data: WizardFormData) => void
  errors: Record<string, string>
}

export default function Step2IntentRouting({ formData, setFormData, errors }: Step2IntentRoutingProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Intent Routing Examples</h2>
        <p className="text-sm text-muted-foreground">
          Define example queries that should be routed to this integration
        </p>
      </div>

      {/* Explanation */}
      <Alert>
        <Lightbulb className="h-4 w-4" />
        <AlertDescription className="text-sm">
          <strong>Why routing examples?</strong>
          <br />
          The AI uses these examples to learn which types of user queries should be handled by this integration
          instead of your knowledge base. The more diverse and specific your examples, the better the routing accuracy.
          <br /><br />
          <strong>Best practices:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Provide 5-10 varied examples that cover different phrasings</li>
            <li>Include questions, commands, and natural language variations</li>
            <li>Be specific to the domain this integration handles</li>
            <li>Avoid generic phrases that could match multiple intents</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Routing Examples Input */}
      <div className="space-y-2">
        <Label htmlFor="routing_examples">Routing Examples *</Label>
        <Textarea
          id="routing_examples"
          rows={10}
          placeholder={`Enter one example per line:\n\nI need to report a pothole on Main Street\nTree fell on my property\nCheck the status of request #12345\nHow do I file a noise complaint?\nWhen will my service request be completed?\nI want to report illegal dumping\nStreetlight is out on Oak Avenue`}
          value={formData.routing_examples}
          onChange={(e) => setFormData({ ...formData, routing_examples: e.target.value })}
          className={errors.routing_examples ? 'border-destructive' : ''}
        />
        <p className="text-xs text-muted-foreground">
          Enter one example query per line. These examples train the intent classifier to route similar queries to this integration.
        </p>
        {errors.routing_examples && (
          <p className="text-sm text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {errors.routing_examples}
          </p>
        )}
      </div>

      {/* Example count indicator */}
      {formData.routing_examples && (
        <div className="text-sm text-muted-foreground">
          {formData.routing_examples.split('\n').filter(line => line.trim()).length} examples provided
          {formData.routing_examples.split('\n').filter(line => line.trim()).length < 5 && (
            <span className="text-amber-600 dark:text-amber-400 ml-2">
              (Recommended: at least 5 examples)
            </span>
          )}
        </div>
      )}

      {/* Additional Context */}
      <div className="p-4 bg-muted/50 rounded-lg border">
        <h3 className="font-medium mb-2 text-sm">How routing works</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
          <li>User sends a message to your assistant</li>
          <li>AI analyzes the message against all integration routing examples</li>
          <li>If a strong match is found, the query is routed to that integration's API</li>
          <li>Otherwise, the query is answered using your knowledge base</li>
        </ol>
      </div>
    </div>
  )
}
